import * as THREE from "three";
import { RoomEnvironment } from "https://unpkg.com/three@0.160.0/examples/jsm/environments/RoomEnvironment.js";

const pin = document.querySelector("[data-relic-pin]");
const section = document.querySelector(".relic");
const canvas = document.querySelector("[data-relic-canvas]");

if (pin && canvas) {
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: "high-performance" });
  } catch (e) {
    renderer = null;
  }

  if (renderer) {
    const isSmall = window.matchMedia("(max-width: 768px)").matches;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const Q = isSmall
      ? { radial: 160, height: 90, lathe: 72, tex: 512, shadow: 1024, dpr: 1.75 }
      : { radial: 288, height: 160, lathe: 128, tex: 1024, shadow: 2048, dpr: 2 };

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, Q.dpr));
    renderer.setClearColor(0x0b0a08, 1);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    const scene = new THREE.Scene();
    scene.fog = new THREE.Fog(0x0b0a08, 7, 19);

    // Soft studio reflections so the marble reads as stone, not plastic
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(renderer), 0.04).texture;
    pmrem.dispose();

    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 100);
    camera.position.set(0, 0.6, 13);

    // ---------- Lighting ----------
    const key = new THREE.DirectionalLight(0xffd9aa, 2.4);
    key.position.set(5, 8, 6);
    key.castShadow = true;
    key.shadow.mapSize.set(Q.shadow, Q.shadow);
    key.shadow.camera.left = -7;
    key.shadow.camera.right = 7;
    key.shadow.camera.top = 7;
    key.shadow.camera.bottom = -7;
    key.shadow.camera.near = 1;
    key.shadow.camera.far = 30;
    key.shadow.bias = -0.0004;
    key.shadow.normalBias = 0.02;
    key.shadow.radius = 4;
    scene.add(key);

    const fill = new THREE.DirectionalLight(0x8fa3c9, 0.45);
    fill.position.set(-7, 2, -3);
    scene.add(fill);

    const rim = new THREE.DirectionalLight(0xffae6b, 1.1);
    rim.position.set(0, 4, -9);
    scene.add(rim);

    const hemi = new THREE.HemisphereLight(0x3a3226, 0x0b0a08, 0.35);
    scene.add(hemi);

    // Warm glow pooled at the feet of the colonnade
    const glow = new THREE.PointLight(0xffb070, 6, 9, 2);
    glow.position.set(0, -2.2, 2.5);
    scene.add(glow);

    // ---------- Procedural marble ----------
    // Seamless 3D value noise sampled on a cylinder so the texture wraps cleanly
    const perm = new Uint8Array(512);
    (() => {
      const p = new Uint8Array(256);
      for (let i = 0; i < 256; i++) p[i] = i;
      let seed = 1337;
      const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
      for (let i = 255; i > 0; i--) {
        const j = Math.floor(rnd() * (i + 1));
        [p[i], p[j]] = [p[j], p[i]];
      }
      for (let i = 0; i < 512; i++) perm[i] = p[i & 255];
    })();
    const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);
    const lerp = (a, b, t) => a + (b - a) * t;
    const hash = (x, y, z) => perm[(perm[(perm[x & 255] + y) & 255] + z) & 255] / 255;
    function noise3(x, y, z) {
      const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
      const xf = x - xi, yf = y - yi, zf = z - zi;
      const u = fade(xf), v = fade(yf), w = fade(zf);
      const x0 = lerp(hash(xi, yi, zi), hash(xi + 1, yi, zi), u);
      const x1 = lerp(hash(xi, yi + 1, zi), hash(xi + 1, yi + 1, zi), u);
      const x2 = lerp(hash(xi, yi, zi + 1), hash(xi + 1, yi, zi + 1), u);
      const x3 = lerp(hash(xi, yi + 1, zi + 1), hash(xi + 1, yi + 1, zi + 1), u);
      return lerp(lerp(x0, x1, v), lerp(x2, x3, v), w);
    }
    function fbm(x, y, z) {
      let sum = 0, amp = 0.5, f = 1;
      for (let o = 0; o < 5; o++) {
        sum += amp * noise3(x * f, y * f, z * f);
        f *= 2.03;
        amp *= 0.5;
      }
      return sum;
    }

    function makeMarbleMaps(size) {
      const w = size, h = size;
      const colorC = document.createElement("canvas");
      const roughC = document.createElement("canvas");
      colorC.width = roughC.width = w;
      colorC.height = roughC.height = h;
      const cctx = colorC.getContext("2d");
      const rctx = roughC.getContext("2d");
      const cImg = cctx.createImageData(w, h);
      const rImg = rctx.createImageData(w, h);

      const base = [238, 229, 212];   // warm ivory
      const vein = [128, 112, 90];    // aged bronze-grey
      const deep = [92, 80, 64];

      for (let y = 0; y < h; y++) {
        const vy = (y / h) * 6.0;
        for (let x = 0; x < w; x++) {
          const a = (x / w) * Math.PI * 2;
          const cx = Math.cos(a) * 1.6, cz = Math.sin(a) * 1.6;
          const n = fbm(cx + 3.1, vy, cz + 7.7);
          const n2 = fbm(cx * 2.4 + 11, vy * 2.4, cz * 2.4 + 5);

          // Primary diagonal veins + a finer secondary network
          const v1 = 1 - Math.abs(Math.sin(vy * 1.3 + cx * 1.1 + n * 7.5));
          const v2 = 1 - Math.abs(Math.sin(vy * 3.1 - cz * 2.0 + n2 * 9.0));
          const veinAmt = Math.pow(v1, 14) * 0.85 + Math.pow(v2, 22) * 0.45;
          const cloud = (n - 0.5) * 0.22;

          const i = (y * w + x) * 4;
          for (let k = 0; k < 3; k++) {
            let c = base[k] * (1 + cloud);
            c = lerp(c, vein[k], Math.min(1, veinAmt));
            c = lerp(c, deep[k], Math.pow(v1, 40) * 0.5);
            cImg.data[i + k] = Math.max(0, Math.min(255, c));
          }
          cImg.data[i + 3] = 255;

          // Veins slightly rougher; polished stone elsewhere, with subtle wear
          const r = 150 + veinAmt * 60 + (n2 - 0.5) * 50;
          rImg.data[i] = rImg.data[i + 1] = rImg.data[i + 2] = Math.max(0, Math.min(255, r));
          rImg.data[i + 3] = 255;
        }
      }
      cctx.putImageData(cImg, 0, 0);
      rctx.putImageData(rImg, 0, 0);

      const map = new THREE.CanvasTexture(colorC);
      map.colorSpace = THREE.SRGBColorSpace;
      const roughnessMap = new THREE.CanvasTexture(roughC);
      [map, roughnessMap].forEach((t) => {
        t.wrapS = THREE.RepeatWrapping;
        t.wrapT = THREE.RepeatWrapping;
        t.anisotropy = renderer.capabilities.getMaxAnisotropy();
      });
      return { map, roughnessMap };
    }

    const marble = makeMarbleMaps(Q.tex);
    const marbleMat = new THREE.MeshPhysicalMaterial({
      map: marble.map,
      roughnessMap: marble.roughnessMap,
      bumpMap: marble.roughnessMap,
      bumpScale: 0.6,
      roughness: 0.55,
      metalness: 0.0,
      clearcoat: 0.18,
      clearcoatRoughness: 0.45,
      envMapIntensity: 0.55,
      sheen: 0.25,
      sheenColor: new THREE.Color(0xfff1dc),
    });
    const trimMat = marbleMat.clone();
    trimMat.color = new THREE.Color(0xf2e9d8);

    // ---------- Geometry ----------
    const SHAFT_H = 5.6;
    const R_BOTTOM = 0.56;
    const R_TOP = 0.47;
    const FLUTES = 20;
    const NUM_DRUMS = isSmall ? 5 : 7; // classical columns were built from stacked stone drums, not one solid shaft

    // Radius along the height, with classical entasis (slight bulge)
    const shaftRadius = (t) => lerp(R_BOTTOM, R_TOP, Math.pow(t, 1.15)) + 0.018 * Math.sin(Math.PI * t);

    // Builds one ring-slice of the fluted shaft spanning [t0, t1] of the full 0-1 profile.
    // Vertex y is baked in absolute column-local space, so each drum mesh can be added at position.y = 0.
    function buildFlutedShaftSegment(t0, t1) {
      const seg = Q.radial;
      const rows = Math.max(3, Math.round(Q.height * (t1 - t0)));
      const positions = [], uvs = [], indices = [];
      for (let j = 0; j <= rows; j++) {
        const t = t0 + (t1 - t0) * (j / rows);
        const y = (t - 0.5) * SHAFT_H;
        const R = shaftRadius(t);
        // Flutes ease out into rounded ends at top and bottom (apophyge)
        const endFade = THREE.MathUtils.smoothstep(t, 0.0, 0.035) * (1 - THREE.MathUtils.smoothstep(t, 0.965, 1.0));
        const depth = R * 0.085 * endFade;
        for (let i = 0; i <= seg; i++) {
          const u = i / seg;
          const a = u * Math.PI * 2;
          const fu = (u * FLUTES) % 1; // position within one flute
          // Concave elliptical groove with a narrow flat fillet between flutes
          const g = (fu - 0.5) / 0.44;
          const groove = Math.abs(g) < 1 ? Math.sqrt(1 - g * g) : 0;
          const r = R - depth * groove;
          positions.push(Math.cos(a) * r, y, Math.sin(a) * r);
          uvs.push(u, t * 2.2);
        }
      }
      for (let j = 0; j < rows; j++) {
        for (let i = 0; i < seg; i++) {
          const a = j * (seg + 1) + i;
          const b = a + seg + 1;
          indices.push(a, b, a + 1, b, b + 1, a + 1);
        }
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
      geo.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
      geo.setIndex(indices);
      geo.computeVertexNormals();
      // Weld the seam normals so there's no visible vertical line
      const n = geo.attributes.normal;
      for (let j = 0; j <= rows; j++) {
        const a = j * (seg + 1), b = a + seg;
        const nx = (n.getX(a) + n.getX(b)) / 2, ny = (n.getY(a) + n.getY(b)) / 2, nz = (n.getZ(a) + n.getZ(b)) / 2;
        n.setXYZ(a, nx, ny, nz);
        n.setXYZ(b, nx, ny, nz);
      }
      return geo;
    }

    // A thin mortar-joint ring marking the seam between two stacked drums
    function buildJointRing(t) {
      const R = shaftRadius(t) + 0.012;
      const y = (t - 0.5) * SHAFT_H;
      const geo = new THREE.TorusGeometry(R, 0.01, 8, isSmall ? 40 : 64);
      geo.rotateX(Math.PI / 2);
      geo.translate(0, y, 0);
      geo.computeVertexNormals();
      return geo;
    }

    // Build a smooth turned profile from [radius, height] control points
    function lathe(profile, stepsPerCurve = 10) {
      const pts = [];
      profile.forEach(([r, y]) => pts.push(new THREE.Vector2(r, y)));
      const curve = new THREE.SplineCurve(pts);
      const smooth = curve.getPoints(pts.length * stepsPerCurve);
      const geo = new THREE.LatheGeometry(smooth, Q.lathe);
      geo.computeVertexNormals();
      return geo;
    }

    // Attic base: torus – scotia – torus
    const baseGeo = lathe([
      [0.0, 0.0],
      [0.74, 0.0],
      [0.8, 0.06],
      [0.8, 0.16],
      [0.74, 0.22],
      [0.66, 0.25],
      [0.63, 0.3],
      [0.66, 0.35],
      [0.64, 0.4],
      [0.6, 0.44],
      [0.56, 0.47],
      [0.0, 0.47],
    ]);
    // Doric-style capital: necking ring, flared echinus
    const capGeo = lathe([
      [0.0, 0.0],
      [0.47, 0.0],
      [0.5, 0.04],
      [0.47, 0.08],
      [0.48, 0.12],
      [0.56, 0.2],
      [0.66, 0.27],
      [0.74, 0.32],
      [0.77, 0.36],
      [0.0, 0.36],
    ]);

    function roundedBox(w, h, d, r) {
      const shape = new THREE.Shape();
      const x = -w / 2, y = -d / 2;
      shape.moveTo(x + r, y);
      shape.lineTo(x + w - r, y);
      shape.quadraticCurveTo(x + w, y, x + w, y + r);
      shape.lineTo(x + w, y + d - r);
      shape.quadraticCurveTo(x + w, y + d, x + w - r, y + d);
      shape.lineTo(x + r, y + d);
      shape.quadraticCurveTo(x, y + d, x, y + d - r);
      shape.lineTo(x, y + r);
      shape.quadraticCurveTo(x, y, x + r, y);
      const geo = new THREE.ExtrudeGeometry(shape, {
        depth: h, bevelEnabled: true, bevelThickness: 0.02, bevelSize: 0.02, bevelSegments: 3, curveSegments: 6,
      });
      geo.rotateX(-Math.PI / 2);
      geo.translate(0, -h / 2, 0);
      geo.computeVertexNormals();
      return geo;
    }

    const drumGeos = [];
    for (let i = 0; i < NUM_DRUMS; i++) {
      drumGeos.push(buildFlutedShaftSegment(i / NUM_DRUMS, (i + 1) / NUM_DRUMS));
    }
    const jointGeos = [];
    for (let i = 1; i < NUM_DRUMS; i++) {
      jointGeos.push(buildJointRing(i / NUM_DRUMS));
    }
    const plinthGeo = roundedBox(1.66, 0.22, 1.66, 0.03);
    const abacusGeo = roundedBox(1.6, 0.2, 1.6, 0.03);

    // Returns { group, pieces } — pieces is the ordered bottom-to-top build sequence
    // used to animate the column assembling drum by drum as the user scrolls.
    function buildColumn() {
      const group = new THREE.Group();
      const pieces = [];
      const add = (geo, mat, y) => {
        const m = new THREE.Mesh(geo, mat);
        m.position.y = y;
        m.castShadow = true;
        m.receiveShadow = true;
        group.add(m);
        pieces.push({ mesh: m, baseY: y });
        return m;
      };
      const half = SHAFT_H / 2;
      add(plinthGeo, trimMat, -half - 0.47 - 0.11);
      add(baseGeo, trimMat, -half - 0.47);
      for (let i = 0; i < NUM_DRUMS; i++) {
        add(drumGeos[i], marbleMat, 0);
        if (i < NUM_DRUMS - 1) add(jointGeos[i], trimMat, 0);
      }
      add(capGeo, trimMat, half);
      add(abacusGeo, trimMat, half + 0.36 + 0.1);
      return { group, pieces };
    }

    const columns = [
      { x: -3.4, z: -0.6, rSpeed: 0.06, tilt: 0.0 },
      { x: 0, z: 1.1, rSpeed: -0.045, tilt: 0.0 },
      { x: 3.4, z: -0.9, rSpeed: 0.05, tilt: 0.0 },
    ].map((cfg, i) => {
      const { group: col, pieces } = buildColumn();
      col.position.set(cfg.x, 0, cfg.z);
      col.userData.rSpeed = cfg.rSpeed;
      col.userData.restY = 0;
      col.userData.delay = i * 0.12;
      col.userData.pieces = pieces;
      scene.add(col);
      return col;
    });

    // Stone floor the columns rise out of — catches soft shadows, fades into fog
    const FLOOR_Y = -SHAFT_H / 2 - 0.47 - 0.22;
    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(30, 64),
      new THREE.MeshStandardMaterial({ color: 0x14110d, roughness: 0.35, metalness: 0.0, envMapIntensity: 0.25 })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = FLOOR_Y;
    floor.receiveShadow = true;
    scene.add(floor);

    // ---------- Dust motes (soft, glowing, drifting upward) ----------
    function makeSpriteTexture() {
      const c = document.createElement("canvas");
      c.width = c.height = 64;
      const ctx = c.getContext("2d");
      const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, "rgba(255,255,255,1)");
      g.addColorStop(0.35, "rgba(255,255,255,0.35)");
      g.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 64);
      const t = new THREE.CanvasTexture(c);
      t.colorSpace = THREE.SRGBColorSpace;
      return t;
    }
    const dustCount = isSmall ? 160 : 320;
    const dustGeo = new THREE.BufferGeometry();
    const dustPos = new Float32Array(dustCount * 3);
    const dustSpeed = new Float32Array(dustCount);
    for (let i = 0; i < dustCount; i++) {
      dustPos[i * 3] = (Math.random() - 0.5) * 16;
      dustPos[i * 3 + 1] = FLOOR_Y + Math.random() * 10;
      dustPos[i * 3 + 2] = (Math.random() - 0.5) * 12;
      dustSpeed[i] = 0.05 + Math.random() * 0.12;
    }
    dustGeo.setAttribute("position", new THREE.BufferAttribute(dustPos, 3));
    const dust = new THREE.Points(dustGeo, new THREE.PointsMaterial({
      color: 0xffd4a0,
      map: makeSpriteTexture(),
      size: 0.07,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    }));
    scene.add(dust);

    // ---------- Resize / input ----------
    function resize() {
      const w = pin.clientWidth, h = pin.clientHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      // Pull back a bit on narrow screens so all three columns stay in frame
      camera.fov = camera.aspect < 0.8 ? 52 : 40;
      camera.updateProjectionMatrix();
    }
    resize();
    window.addEventListener("resize", resize);

    const mouse = { x: 0, y: 0 };
    const mouseEased = { x: 0, y: 0 };
    window.addEventListener("mousemove", (e) => {
      mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.y = (e.clientY / window.innerHeight) * 2 - 1;
    });

    let progress = 0;
    let progressEased = 0;
    function updateProgress() {
      const rect = section.getBoundingClientRect();
      const scrollable = rect.height - window.innerHeight;
      progress = scrollable > 0 ? Math.min(1, Math.max(0, -rect.top / scrollable)) : 0;
    }

    let visible = true;
    if ("IntersectionObserver" in window) {
      new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; }, { threshold: 0 })
        .observe(pin);
    }

    const easeInOut = (x) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);
    // Overshoots past 1 then settles back — reads as a stone block dropping into place with a thud
    const easeOutBack = (x) => {
      const c1 = 1.70158, c3 = c1 + 1;
      return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
    };

    const clock = new THREE.Clock();
    let last = 0;
    function tick() {
      requestAnimationFrame(tick);
      if (!visible) return;
      updateProgress();
      const t = clock.getElapsedTime();
      const dt = Math.min(0.05, t - last);
      last = t;

      // Smooth out scroll so the motion glides instead of stepping
      progressEased += (progress - progressEased) * (reduceMotion ? 1 : 0.08);
      const p = progressEased;

      mouseEased.x += (mouse.x - mouseEased.x) * 0.035;
      mouseEased.y += (mouse.y - mouseEased.y) * 0.035;

      camera.position.z = THREE.MathUtils.lerp(10, 4.2, easeInOut(p));
      camera.position.y = THREE.MathUtils.lerp(0.4, 1.5, p) + mouseEased.y * 0.15;
      camera.position.x = mouseEased.x * 0.45 + Math.sin(t * 0.15) * 0.08;
      camera.lookAt(0, THREE.MathUtils.lerp(0.2, 0.8, p), 0);

      columns.forEach((col) => {
        const localP = THREE.MathUtils.clamp((p - col.userData.delay) / (1 - col.userData.delay), 0, 1);
        col.position.y = col.userData.restY;
        const spin = reduceMotion ? 0 : t * col.userData.rSpeed;
        col.rotation.y = spin;

        // Build the column bottom-to-top, one piece at a time, as localP advances with scroll
        const pieces = col.userData.pieces;
        const span = 1 / pieces.length;
        const duration = span * 1.7;
        pieces.forEach((piece, i) => {
          const start = i * span * 0.82;
          const rawT = THREE.MathUtils.clamp((localP - start) / duration, 0, 1);
          const pieceT = reduceMotion ? (rawT > 0 ? 1 : 0) : rawT;
          const eased = easeOutBack(pieceT);
          piece.mesh.visible = pieceT > 0.001;
          piece.mesh.position.y = piece.baseY - (1 - eased) * 1.1;
          const s = THREE.MathUtils.clamp(0.7 + eased * 0.3, 0.7, 1);
          piece.mesh.scale.setScalar(s);
        });
      });

      if (!reduceMotion) {
        const pos = dustGeo.attributes.position;
        for (let i = 0; i < dustCount; i++) {
          let y = pos.getY(i) + dustSpeed[i] * dt;
          if (y > FLOOR_Y + 10) y = FLOOR_Y;
          pos.setY(i, y);
        }
        pos.needsUpdate = true;
        dust.rotation.y = t * 0.012;
      }

      key.intensity = 2.0 + p * 0.8;
      rim.intensity = 0.8 + p * 1.0;
      glow.intensity = 3 + p * 6 + Math.sin(t * 1.3) * 0.4;

      renderer.render(scene, camera);
    }

    tick();
  }
}
